import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TextUtils } from '@cyberint/common-utils';
import { Themes } from '../../style/theme-consts';
import { CustomTooltip } from '../Poppers';
import * as S from './styled';
import { getAvatarStyles, sizes } from './styled';
import { anchorOrigins, isValidUrl } from './utils';

const AvatarBadge = ({
  files = [],
  badgeContent,
  position = 'bottom-right',
  size = 'medium',
  mode = Themes.LIGHT,
  testId,
  onClick
}) => {
  const [imageError, setImageError] = useState({});
  const mainFile = files[0];
  const extraFiles = files.slice(1);
  const hasExtraAvatars = extraFiles.length > 0;

  const mainInitials = useMemo(() => TextUtils.getInitials(mainFile?.name[0]), [mainFile]);
  const showMainImage = useMemo(
    () => mainFile?.url && isValidUrl(mainFile.url) && !imageError[0],
    [mainFile, imageError]
  );

  const onAvatarClickHandler = file => () => {
    if (onClick && file) {
      onClick(file);
    }
  };

  const imageErrorHandler = index => () => {
    setImageError(prev => ({ ...prev, [index]: true }));
  };

  const tooltipContent = hasExtraAvatars && (
    <S.TooltipContent $extraFilesLength={extraFiles.length}>
      {extraFiles.map((file, index) => {
        return (
          <S.AvatarItem key={file.url || index}>
            <S.Avatar
              src={isValidUrl(file.url) && !imageError[index + 1] ? file.url : undefined}
              alt={file.name || ''}
              sx={getAvatarStyles(size)}
              onClick={onAvatarClickHandler(file)}
              onError={imageErrorHandler(index + 1)}>
              {TextUtils.getInitials(file.name)}
            </S.Avatar>
          </S.AvatarItem>
        );
      })}
    </S.TooltipContent>
  );

  const badgeElement = hasExtraAvatars ? (
    <CustomTooltip mode={mode} title={tooltipContent} placement={position.split('-')[1]}>
      <span>+{extraFiles.length}</span>
    </CustomTooltip>
  ) : (
    badgeContent
  );

  return (
    <S.Badge
      overlap="circular"
      anchorOrigin={anchorOrigins[position]}
      badgeContent={badgeElement}
      $badgeSize={sizes[size].badge}
      $hasExtraAvatars={hasExtraAvatars}>
      <S.Avatar
        alt={mainFile?.name || ''}
        src={showMainImage ? mainFile.url : undefined}
        sx={getAvatarStyles(size)}
        data-testid={testId}
        onClick={onAvatarClickHandler(mainFile)}
        onError={imageErrorHandler(0)}>
        {!showMainImage && mainInitials}
      </S.Avatar>
    </S.Badge>
  );
};

AvatarBadge.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ),
  testId: PropTypes.string,
  badgeContent: PropTypes.node,
  position: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  mode: PropTypes.oneOf([Themes.LIGHT, Themes.DARK]),
  onClick: PropTypes.func
};

export default AvatarBadge;
